// src/components/Reports/PromoSalesModal.js
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import SecureLS from "secure-ls";
import { getAuthCredentials } from "../../../utils/auth";

const ls = new SecureLS({ encodingType: "aes" });

const PromoSalesModal = ({ open, onClose }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { token } = getAuthCredentials();

  const handleGenerate = async () => {
    /* quick client-side validation ------------------------------------ */
    if (!startDate || !endDate) {
      Swal.fire("Validation Error", "Please select both dates.", "warning");
      return;
    }
    if (startDate > endDate) {
      Swal.fire(
        "Validation Error",
        "Start date cannot be after end date.",
        "warning"
      );
      return;
    }

    try {
      setIsLoading(true);

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/reports/promo-sales`,
        { startDate, endDate },
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const storeName = (ls.get("user")?.storeName || "Store").replace(
        /\s+/g,
        "_"
      );
      const filename = `${storeName}_Promo_Sales_Report_${startDate}_to_${endDate}.xlsx`;

      /* trigger download ---------------------------------------------- */
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();

      Swal.fire(
        "Success",
        "Promo Sales Report generated successfully!",
        "success"
      );
      onClose();
    } catch (err) {
      Swal.fire(
        "Error",
        err.response?.data?.message || "Failed to generate Promo Sales Report.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Generate Promotional&nbsp;Sales&nbsp;Report</DialogTitle>

      <DialogContent>
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          fullWidth
          sx={{ my: 2 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Generate"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromoSalesModal;
